import {
  FIELDS_LIST_CHANGED_EVENT,
  FIELDS_MFE_AGGREGATES_DELAY_MS,
  FIELDS_MFE_REFRESH_DEBOUNCE_MS,
  SEASON_MGT_CROPZONES_CHANGED_EVENT,
} from '@core/constants/fieldsMfe';
import type { CropzonesChangedDetail, FieldsListChangedDetail } from '@core/models/fieldsMfe';
import type { Property } from '@core/models/properties';
import type { FieldSeasonData } from '@core/models/seasons';
import { dashboardQueryKeys } from '@hooks/queries/dashboard/dashboard.constants';
import { selectedSceneGroup, selectedSideSceneGroup } from '@redux/imageAssets/selectors';
import { loadAssetsAndImages } from '@redux/imageAssets/thunks';
import type { RootState } from '@redux/reducers';
import { store as reduxStore } from '@redux/store';
import { useBoundStore } from '@state/store';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from '@utils/dateUtils';
import { useEffect } from 'react';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

type PropertyEvent = CustomEvent<Partial<FieldsListChangedDetail | CropzonesChangedDetail> | null>;

function refreshSelectedSeasonImagery(
  propertyId: string,
  previousSeason: FieldSeasonData,
  previousCatalog: RootState['ImageAsset']['sceneCancelToken']
) {
  const { regions, seasons } = useBoundStore.getState();
  if (regions.propertyId !== propertyId || regions.status !== 'success') return;
  if (regions.selectedRegionId !== 'root' || seasons.selectedSeasonId !== previousSeason.id) return;

  const nextSeason = seasons.entities[previousSeason.id];
  if (!nextSeason) return;
  const previousFields = Object.keys(previousSeason.fieldsSeason).filter(
    (fieldId) => !previousSeason.fieldsSeason[fieldId].deleted
  );
  const nextFields = new Set(
    Object.keys(nextSeason.fieldsSeason).filter(
      (fieldId) => !nextSeason.fieldsSeason[fieldId].deleted
    )
  );
  const membershipChanged =
    previousFields.length !== nextFields.size ||
    previousFields.some((fieldId) => !nextFields.has(fieldId));
  if (!membershipChanged) return;

  const reduxState = reduxStore.getState();
  if (reduxState.ImageAsset.sceneCancelToken !== previousCatalog) return;
  const scene = selectedSceneGroup(reduxState);
  const sideScene = selectedSideSceneGroup(reduxState);
  const defaultImage = (scene ?? sideScene)?.referenceImage;
  const sideImage = (sideScene ?? scene)?.referenceImage;
  void (reduxStore.dispatch as ThunkDispatch<RootState, undefined, AnyAction>)(
    loadAssetsAndImages(
      propertyId,
      defaultImage && sideImage
        ? {
            default: {
              date: dayjs(defaultImage.acquiredAt).startOf('d'),
              source: defaultImage.source,
            },
            side: { date: dayjs(sideImage.acquiredAt).startOf('d'), source: sideImage.source },
          }
        : undefined
    )
  );
}

/**
 * Keeps fields and seasons in sync with changes made by the Fields and Season Management MFEs,
 * without the full reload RootLayout runs when the property changes.
 */
export function useRefreshOnFieldsMfeEvents(property?: Property) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!property) return;

    let fieldsChanged = false;
    let seasonsChanged = false;
    let refreshInFlight = false;
    let pendingFieldsInvalidation = false;
    let pendingSeasonsInvalidation = false;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    let invalidationTimer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;

    // Never invalidate ['property']: a new property object makes RootLayout reset and reload
    // everything, which is exactly what this hook avoids.
    const invalidateAggregates = () => {
      if (pendingFieldsInvalidation) {
        void queryClient.invalidateQueries({ queryKey: ['properties'] });
        void queryClient.invalidateQueries({ queryKey: ['get-farms-regiontree'] });
      }
      if (pendingSeasonsInvalidation) {
        void queryClient.invalidateQueries({ queryKey: ['baselines', { property: property.id }] });
      }
      if (pendingFieldsInvalidation || pendingSeasonsInvalidation) {
        void queryClient.invalidateQueries({
          queryKey: dashboardQueryKeys.cropSummariesAll(),
          refetchType: 'all',
        });
      }
      pendingFieldsInvalidation = false;
      pendingSeasonsInvalidation = false;
    };

    const refresh = async () => {
      if (refreshInFlight || disposed) return;

      const refreshFields = fieldsChanged;
      const refreshSeasons = seasonsChanged;
      if (!refreshFields && !refreshSeasons) return;

      fieldsChanged = false;
      seasonsChanged = false;
      refreshInFlight = true;

      try {
        const { seasons } = useBoundStore.getState();
        const previousSeason = seasons.entities[seasons.selectedSeasonId ?? ''];
        const previousCatalog = reduxStore.getState().ImageAsset.sceneCancelToken;
        const nextSeasons = refreshSeasons ? await seasons.refresh(property) : undefined;
        if (disposed) return;
        if (refreshSeasons && !nextSeasons && !refreshFields) return;

        const previousRegions = useBoundStore.getState().regions.entities;
        await useBoundStore
          .getState()
          .regions.refresh(
            property,
            nextSeasons ?? Object.values(useBoundStore.getState().seasons.entities),
            { skipCache: refreshFields }
          );
        if (disposed) return;

        pendingFieldsInvalidation ||= refreshFields;
        pendingSeasonsInvalidation ||= refreshSeasons && nextSeasons !== undefined;
        clearTimeout(invalidationTimer);
        invalidationTimer = setTimeout(invalidateAggregates, FIELDS_MFE_AGGREGATES_DELAY_MS);

        if (
          nextSeasons &&
          previousSeason &&
          previousRegions !== useBoundStore.getState().regions.entities
        ) {
          refreshSelectedSeasonImagery(property.id, previousSeason, previousCatalog);
        }
      } finally {
        refreshInFlight = false;
        if (!disposed && (fieldsChanged || seasonsChanged)) scheduleRefresh();
      }
    };

    const scheduleRefresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => void refresh(), FIELDS_MFE_REFRESH_DEBOUNCE_MS);
    };

    const isForProperty = (event: Event) =>
      (event as PropertyEvent).detail?.propertyId === property.id;

    const onFieldsChanged = (event: Event) => {
      if (!isForProperty(event)) return;
      fieldsChanged = true;
      scheduleRefresh();
    };

    const onCropzonesChanged = (event: Event) => {
      if (!isForProperty(event)) return;
      seasonsChanged = true;
      scheduleRefresh();
    };

    globalThis.addEventListener(FIELDS_LIST_CHANGED_EVENT, onFieldsChanged);
    globalThis.addEventListener(SEASON_MGT_CROPZONES_CHANGED_EVENT, onCropzonesChanged);

    return () => {
      disposed = true;
      globalThis.removeEventListener(FIELDS_LIST_CHANGED_EVENT, onFieldsChanged);
      globalThis.removeEventListener(SEASON_MGT_CROPZONES_CHANGED_EVENT, onCropzonesChanged);
      clearTimeout(refreshTimer);
    };
  }, [property, queryClient]);
}
